import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ProjectService } from '../../shared/services/project/project.service';
import { AppStateService } from '../../shared/services/app-state/app-state.service';
import { ProjectEventsService } from '../../shared/services/project-events/project-events.service';
import { ProjectBody } from '@app/shared/interfaces';
import { FormValidationUtils } from '../../shared/utils/form-validation.utils';
import { LocalstorageHelper } from '../../shared/helpers/localstorage.helper';

import { MatDialogModule } from '@angular/material/dialog';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-project-create-dialog',
  templateUrl: './project-create-dialog.component.html',
  styleUrls: ['./project-create-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
  ],
})
export class ProjectCreateDialogComponent {
  private fb = inject(FormBuilder);
  private projectService = inject(ProjectService);
  private appStateService = inject(AppStateService);
  private readonly projectEvents = inject(ProjectEventsService);
  private dialogRef = inject<MatDialogRef<ProjectCreateDialogComponent>>(MatDialogRef);

  projectForm: FormGroup;
  isSubmitting = signal(false);
  errorMessage = signal('');

  constructor() {
    this.projectForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      description: [''],
      workflowName: ['', [Validators.required, Validators.minLength(2)]],
    });
  }

  onSubmit(): void {
    if (this.projectForm.invalid || this.isSubmitting()) {
      // Mark all fields as touched to show validation errors
      FormValidationUtils.markAllFieldsAsTouched(this.projectForm);
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessage.set('');

    const projectData: ProjectBody = this.projectForm.value;

    this.projectService.createProject(projectData).subscribe({
      next: (result) => {
        this.isSubmitting.set(false);

        // Notify the projects list to refresh
        this.projectEvents.notifyCreated(result.projectId);

        const versionId = result.currentVersionId || result.versions?.[0]?.versionId;

        // Navigate to the created project. The create response does not always
        // carry version info (dev/stage backends return no currentVersionId and
        // an empty versions[]), so navigation must NOT depend on resolving a
        // versionId here. When we have one, pass it as a fast path and remember
        // it; otherwise navigate to the project and let projectGuard resolve the
        // head version via GET /project/:id (EHQ-194).
        if (result.projectId) {
          if (versionId) {
            LocalstorageHelper.setLastInteractedVersion(
              result.projectId,
              versionId,
            );
          }
          this.appStateService.navigateToProject(
            result.projectId,
            versionId,
          );
        }

        this.dialogRef.close(result);
      },
      error: (error) => {
        this.isSubmitting.set(false);
        this.errorMessage.set(
          error?.error?.message || error?.message || 'Failed to create project. Please try again.',
        );
        console.error('Error creating project:', error);
      },
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
